import $ from 'jquery';
import { AI_FEATURE_CATEGORIES } from './aiFeatureCategories';
import { generateSearchBoxHTML, initAiFeatureSearch } from './aiFeatureSearch';
import {
  isBlackFridayActive,
  isLast24Hours,
  getTimeRemaining,
} from '~src/sidebar/initAiBlackFridayModal';

// Store interval reference for cleanup
let countdownInterval = null;

/**
 * Generate HTML for a single AI feature
 */
function generateFeatureHTML(feature, index) {
  const paddingClass = index % 2 === 0 ? 'pl-3 pr-2' : 'pl-2 pr-3';

  // Special features that have their own handlers should not use data-ai-edit
  const specialFeatures = [
    'change-clothes',
    'magic-eraser',
    'blurbg',
    'removebg',
    'expand',
    'imageEnhancement',
    'colorizebw',
    'smile',
    'makeLookYounger',
    'faceEnhancement',
    'dissolvenza',
    'background-fusion',
  ];
  const shouldUseDataAiEdit =
    feature.prompt && !specialFeatures.includes(feature.id);

  return `
    <div class="col-6 mb-3 ${paddingClass} text-center" data-search-feature-id="${
    feature.id
  }">
      <div
        class="ai-feature-wrapper"
        role="button"
        id="editor-menu-image-${feature.id}"
        ${shouldUseDataAiEdit ? `data-ai-edit="${feature.prompt}"` : ''}
      >
        <img
          src="/assets/svg/img-loader.gif"
          data-src="${feature.image}"
          class="img-fluid rounded lazy img-ai-tool"
          alt="${feature.title}"
          draggable="false"
        />
        <small class="caption-text">${feature.caption || feature.title}</small>
      </div>
      <button class="btn btn-sm btn-outline-info ai-feature-help-btn mt-1" data-feature="${
        feature.id
      }" title="Info su ${feature.title}">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 16V16.01M12 13V11C12 10.4477 12.4477 10 13 10C13.5523 10 14 9.55228 14 9C14 8.44772 13.5523 8 13 8C12.4477 8 12 8.44772 12 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="ml-1">Info</span>
      </button>
    </div>
  `;
}

/**
 * Generate HTML for a category box
 */
function generateCategoryHTML(category) {
  const featuresHTML = category.features
    .map((feature, index) => generateFeatureHTML(feature, index))
    .join('');

  return `
    <div class="sidebar-box" data-search-category-id="${category.id}">
      <div class="sidebar-subtitle">
        ${category.title}
        <small class="text-muted ai-feature-count">(${category.features.length} strumenti)</small>
      </div>
      <div class="row">
        ${featuresHTML}
      </div>
    </div>
  `;
}

/**
 * Render the entire AI sidebar
 */
export function renderAiSidebar() {
  const container = $('#tab-edit-images-ai');
  if (!container.length) {
    console.warn('AI sidebar container not found');
    return;
  }

  // Generate HTML for all categories
  const categoriesHTML = AI_FEATURE_CATEGORIES.map((category) =>
    generateCategoryHTML(category)
  ).join('');

  // Generate Black Friday banner HTML if promo is active
  const showCountdown = isLast24Hours();
  const blackFridayBannerHTML = isBlackFridayActive()
    ? `
      <div class="bf-sidebar-banner" id="bf-sidebar-banner" role="button" data-toggle="modal" data-target="#aiBlackFridayModal">
        <div class="bf-banner-content">
          <div class="bf-banner-badge">BLACK FRIDAY</div>
          <div class="bf-banner-title">OFFERTA SPECIALE</div>
          <div class="bf-banner-subtitle">Risparmia sui Crediti AI</div>
          ${
            showCountdown
              ? `
            <div class="bf-banner-countdown" id="bf-sidebar-countdown">
              <div class="bf-countdown-unit">
                <span class="bf-countdown-value" id="bf-countdown-days">00</span>
                <span class="bf-countdown-label">giorni</span>
              </div>
              <span class="bf-countdown-separator">:</span>
              <div class="bf-countdown-unit">
                <span class="bf-countdown-value" id="bf-countdown-hours">00</span>
                <span class="bf-countdown-label">ore</span>
              </div>
              <span class="bf-countdown-separator">:</span>
              <div class="bf-countdown-unit">
                <span class="bf-countdown-value" id="bf-countdown-minutes">00</span>
                <span class="bf-countdown-label">min</span>
              </div>
            </div>
          `
              : ''
          }
          <div class="bf-banner-cta">Scopri le Offerte</div>
        </div>
      </div>
    `
    : '';

  // Full sidebar HTML
  const sidebarHTML = `
    <div class="sidebar-box">
      <div class="sidebar-subtitle">
        Intelligenze Artificiali
      </div>
      ${blackFridayBannerHTML}
      <div class="mb-3">
        <small class="text-muted">Crediti disponibili:</small>
        <span class="badge badge-primary ml-1" id="ai-credits-balance">0</span>
        <a
          href="/credits"
          target="_blank"
          class="btn btn-sm btn-link float-right p-1"
          title="Visualizza storico utilizzo"
          data-toggle="tooltip"
          style="color: #6c757d;"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0Z"/>
            <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6.5a.5.5 0 0 1-1 0V1H3v14h3.5a.5.5 0 0 1 0 1H3a1 1 0 0 1-1-1V1Z"/>
            <path d="M4.5 6.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5ZM4.5 9a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1H5A.5.5 0 0 1 4.5 9ZM4.5 11.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5Z"/>
          </svg>
        </a>
      </div>
      <div class="row">
        <div class="col-12">
          <button
            class="btn btn-sm btn-block btn-outline-light"
            data-toggle="modal"
            data-target="#aiCreditPackagesModal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="mr-1"
              viewBox="0 0 16 16"
            >
              <path d="M3 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H3zm1 5.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5z"/>
            </svg>
            Acquista Crediti
          </button>
          <button
            class="btn btn-sm btn-block btn-outline-warning mt-2"
            id="ai-guided-tour-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="mr-1"
              viewBox="0 0 16 16"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
            </svg>            
            Scopri tutti gli strumenti
          </button>
          <button
            class="btn btn-sm btn-block btn-outline-secondary mt-2"
            id="ai-credits-info-btn"
            data-toggle="modal"
            data-target="#aiCreditsInfoModal"
          >
          <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="mr-1"
              viewBox="0 0 16 16"
            >
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
            </svg>
            Come funzionano i crediti
          </button>
        </div>
      </div>
    </div>
    ${generateSearchBoxHTML()}
    ${categoriesHTML}
  `;

  // Replace container content
  container.html(sidebarHTML);

  // Initialize help functionality first
  const { initAiIndividualHelp } = require('./aiIndividualHelp');
  initAiIndividualHelp();

  // Initialize guided tour functionality
  const { initAiGuidedTour } = require('./aiGuidedTour');
  initAiGuidedTour();

  // Initialize feature search functionality
  initAiFeatureSearch();

  // Trigger lazy loading with a small delay to ensure DOM is ready
  setTimeout(() => {
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update();
    } else {
      console.warn('LazyLoad instance not found, loading images directly');
      // Fallback: load images directly if LazyLoad is not available
      $('.lazy').each(function () {
        const $img = $(this);
        const src = $img.attr('data-src');
        if (src) {
          $img.attr('src', src);
          $img.removeClass('lazy');
        }
      });
    }
  }, 50);

  // Initialize countdown timer if within threshold and promo is active
  if (showCountdown && isBlackFridayActive()) {
    startCountdownTimer(); // eslint-disable-line no-use-before-define
  }
}

/**
 * Start the countdown timer for the sidebar banner
 */
function startCountdownTimer() {
  // Clear any existing interval
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }

  const updateCountdown = () => {
    const remaining = getTimeRemaining();

    if (!remaining) {
      // Promo ended, clear interval and refresh sidebar
      clearInterval(countdownInterval);
      countdownInterval = null;
      renderAiSidebar();
      return;
    }

    const pad = (n) => String(n).padStart(2, '0');

    $('#bf-countdown-days').text(pad(remaining.days));
    $('#bf-countdown-hours').text(pad(remaining.hours));
    $('#bf-countdown-minutes').text(pad(remaining.minutes));
  };

  // Initial update
  updateCountdown();

  // Update every second
  countdownInterval = setInterval(updateCountdown, 1000);
}

/**
 * Update specific category features (for dynamic updates)
 */
export function updateCategoryFeatures(categoryId, features) {
  const category = AI_FEATURE_CATEGORIES.find((cat) => cat.id === categoryId);
  if (category) {
    category.features = features;
    renderAiSidebar();
  }
}
